<template>
    <div id="main-container">
        <div class="page-header">
            <h1 id="page-title">Sincronizzazione Database Configurazioni</h1>

            <Button label="Nuovo gruppo" icon="pi pi-plus" @click="openGroupModal" />
        </div>

        <div class="sync-search-box">
            <span class="p-input-icon-left sync-search-field">
                <i class="pi pi-search" />
                <InputText
                    v-model="tableSearch"
                    placeholder="Cerca una tabella per sapere se e dove viene sincronizzata"
                />
            </span>

            <Button
                v-if="tableSearch"
                icon="pi pi-times"
                class="p-button-text p-button-sm"
                title="Pulisci"
                @click="tableSearch = ''"
            />
        </div>

        <div v-if="isSearching" class="sync-search-results">
            <div v-if="searchResults.length === 0" class="sync-search-empty">
                Nessun gruppo sincronizza una tabella con questo nome.
            </div>

            <template v-else>
                <div class="sync-search-count">
                    {{ searchResults.length }} risultat{{ searchResults.length === 1 ? 'o' : 'i' }}
                </div>

                <div
                    v-for="result in searchResults"
                    :key="result.groupId + '-' + result.tabella"
                    class="sync-search-row"
                    title="Apri il gruppo"
                    @click="openGroupFromSearch(result)"
                >
                    <div class="sync-search-main">
                        <strong>{{ result.tabella }}</strong>
                        <span class="sync-search-group">{{ result.gruppo }}</span>
                        <span :class="result.statoClass">{{ result.stato }}</span>
                    </div>

                    <div class="sync-search-details">
                        <span>{{ result.tipo }}</span>
                        <span>{{ result.sorgente }} &rarr; {{ result.destinazione }}</span>
                        <span>{{ result.cron }}</span>
                        <span v-if="result.colonneChiave">chiavi: {{ result.colonneChiave }}</span>
                    </div>
                </div>
            </template>
        </div>

        <div v-if="loading" class="loading-box">
            Caricamento gruppi...
        </div>

        <div v-else-if="groups.length === 0" class="empty-box">
            Nessun gruppo di sincronizzazione configurato.
        </div>

        <Accordion v-else v-model:activeIndex="activeGroupIndex">
            <AccordionTab v-for="group in groups" :key="group.id">
                <template #header>
                    <div class="sync-group-header">
                        <div class="sync-group-header-left">
                            <strong>{{ group.gruppo }}</strong>

                            <span class="sync-group-table-count">
                                {{ getTablesCount(group) }} tabelle
                            </span>

                            <span :class="getGroupStatusClass(group)">
                                {{ getGroupStatusLabel(group) }}
                            </span>

                            <span :class="group.attivo ? 'sync-group-active' : 'sync-group-inactive'">
                                {{ group.attivo ? 'Attivo' : 'Disattivo' }}
                            </span>
                        </div>

                        <div class="sync-group-header-actions">
                            <Button
                                icon="pi pi-play"
                                class="p-button-success p-button-sm"
                                title="Esegui ora"
                                :loading="runningGroupId === group.id"
                                :disabled="!!group.in_esecuzione || !!group.in_coda || !group.attivo"
                                @click.stop="runGroupNow(group)"
                            />

                            <Button
                                icon="pi pi-pencil"
                                class="p-button-warning p-button-sm"
                                title="Modifica gruppo"
                                :disabled="!!group.in_esecuzione"
                                @click.stop="openEditGroupModal(group)"
                            />

                            <Button
                                icon="pi pi-trash"
                                class="p-button-danger p-button-sm"
                                title="Elimina gruppo"
                                :disabled="!!group.in_esecuzione"
                                @click.stop="deleteGroup(group.id)"
                            />
                        </div>
                    </div>
                </template>

                <div class="group-info">
                    <div><strong>Tipo:</strong> {{ group.tipo }}</div>
                    <div><strong>Sorgente:</strong> {{ group.sorgente_nome }}</div>
                    <div><strong>Destinazione:</strong> {{ group.destinazione_nome }}</div>
                    <div>
                        <strong>Pianificazione:</strong>
                        <code class="sync-cron-value">{{ group.cron_expression }}</code>
                    </div>
                    <div><strong>Prossimo avvio:</strong> {{ group.prossimo_avvio || '-' }}</div>
                    <div><strong>Stato:</strong> {{ getGroupStatusLabel(group) }}</div>

                    <div class="group-active-box">
                        <strong>Attivo:</strong>

                        <InputSwitch
                            :modelValue="!!group.attivo"
                            :disabled="!!group.in_esecuzione"
                            @update:modelValue="updateGroupStatus(group, $event)"
                        />
                    </div>
                </div>

                <div class="notifications-box">
                    <div class="notifications-info">
                        <strong>Notifiche email:</strong>

                        <template v-if="hasNotificationsEnabled(group)">
                            <span v-if="group.notifiche.destinatari_riepilogo" class="sync-notify-line">
                                <span class="sync-notify-badge sync-notify-badge-summary">Riepilogo</span>
                                <span class="sync-notify-recipients">{{ group.notifiche.destinatari_riepilogo }}</span>
                            </span>

                            <span v-if="group.notifiche.destinatari_errori" class="sync-notify-line">
                                <span class="sync-notify-badge sync-notify-badge-error">Errori</span>
                                <span class="sync-notify-recipients">{{ group.notifiche.destinatari_errori }}</span>
                            </span>
                        </template>

                        <span v-else class="sync-notify-off">Disattivate</span>
                    </div>

                    <Button
                        icon="pi pi-envelope"
                        label="Configura"
                        class="p-button-sm p-button-outlined"
                        @click="openNotificationsModal(group)"
                    />
                </div>

                <div class="table-header">
                    <h3>Tabelle</h3>

                    <Button
                        label="Aggiungi tabella"
                        icon="pi pi-plus"
                        class="p-button-sm"
                        :disabled="!!group.in_esecuzione"
                        @click="openTableModal(group)"
                    />
                </div>

                <DataTable
                    :value="group.tabelle || []"
                    responsiveLayout="scroll"
                    class="sync-table"
                    :rowClass="getTableRowClass"
                    :scrollable="true"
                    scrollHeight="420px"
                >
                    <template #empty>
                        Nessuna tabella inserita
                    </template>

                    <Column field="ordine" header="Ordine" />
                    <Column field="tabella" header="Tabella" />
                    <Column field="colonne_chiave" header="Colonne chiave" />

                    <Column header="Attivo">
                        <template #body="slotProps">
                            <InputSwitch
                                :modelValue="!!slotProps.data.attivo"
                                :disabled="!!group.in_esecuzione"
                                @update:modelValue="updateTableStatus(slotProps.data, $event)"
                            />
                        </template>
                    </Column>

                    <Column header="Azioni">
                        <template #body="slotProps">
                            <div class="sync-table-actions">
                                <Button
                                    icon="pi pi-pencil"
                                    class="p-button-warning p-button-sm"
                                    title="Modifica tabella"
                                    :disabled="!!group.in_esecuzione"
                                    @click="openEditTableModal(group, slotProps.data)"
                                />

                                <Button
                                    icon="pi pi-trash"
                                    class="p-button-danger p-button-sm"
                                    title="Elimina tabella"
                                    :disabled="!!group.in_esecuzione"
                                    @click="deleteTable(slotProps.data.id)"
                                />
                            </div>
                        </template>
                    </Column>
                </DataTable>
            </AccordionTab>
        </Accordion>

        <Dialog
            :header="groupModalTitle"
            v-model:visible="showGroupModal"
            :modal="true"
            :style="{ width: '650px' }"
        >
            <div class="form-grid">
                <div class="field">
                    <label>Gruppo</label>
                    <InputText v-model="groupForm.gruppo" />
                </div>

                <div class="field">
                    <label>
                        Tipo
                        <i
                            class="pi pi-question-circle sync-help-icon"
                            @click="showTypesInfoModal = true"
                        ></i>
                    </label>

                    <Dropdown
                        v-model="groupForm.database_sync_type_id"
                        :options="syncTypes"
                        optionLabel="nome"
                        optionValue="id"
                        placeholder="Seleziona tipo"
                        :disabled="editingGroupId !== null"
                    />
                </div>

                <div class="field">
                    <label>Sorgente</label>
                    <Dropdown
                        v-model="groupForm.sorgente"
                        :options="databases"
                        optionLabel="dbname"
                        optionValue="id"
                        placeholder="Seleziona database sorgente"
                    />
                </div>

                <div class="field">
                    <label>Destinazione</label>
                    <Dropdown
                        v-model="groupForm.destinazione"
                        :options="databases"
                        optionLabel="dbname"
                        optionValue="id"
                        placeholder="Seleziona database destinazione"
                    />
                </div>

                <div class="field field-full">
                    <label>Pianificazione (cron)</label>

                    <InputText
                        v-model="groupForm.cron_expression"
                        placeholder="*/5 * * * *"
                        class="sync-cron-input"
                        @input="onCronExpressionInput"
                    />

                    <small class="sync-cron-legend">
                        5 campi separati da spazio: minuto, ora, giorno del mese, mese, giorno della settimana
                    </small>

                    <div class="sync-cron-presets">
                        <button
                            v-for="preset in cronPresets"
                            :key="preset.value"
                            type="button"
                            class="sync-cron-preset-button"
                            :class="{ 'sync-cron-preset-button-active': groupForm.cron_expression === preset.value }"
                            :title="preset.value"
                            @click="applyCronPreset(preset.value)"
                        >
                            {{ preset.label }}
                        </button>
                    </div>

                    <div v-if="cronPreviewLoading" class="sync-cron-preview sync-cron-preview-loading">
                        Verifica in corso...
                    </div>

                    <div
                        v-else-if="groupForm.cron_expression"
                        class="sync-cron-preview"
                        :class="cronPreviewValid ? 'sync-cron-preview-valid' : 'sync-cron-preview-invalid'"
                    >
                        <template v-if="cronPreviewValid">
                            <strong>Prossime esecuzioni:</strong>
                            <span v-for="data in cronPreviewDates" :key="data" class="sync-cron-date">
                                {{ data }}
                            </span>
                        </template>

                        <template v-else>
                            Espressione non valida
                        </template>
                    </div>
                </div>

                <div class="field">
                    <label>Attivo</label>
                    <InputSwitch v-model="groupForm.attivo" />
                </div>
            </div>

            <template #footer>
                <Button label="Annulla" class="p-button-secondary" @click="closeGroupModal" />
                <Button label="Salva" icon="pi pi-save" :loading="savingGroup" @click="saveGroup" />
            </template>
        </Dialog>

        <Dialog
            :header="tableModalTitle"
            v-model:visible="showTableModal"
            :modal="true"
            :style="{ width: '550px' }"
        >
            <div v-if="isSelectedGroupDump && !editingTableId">
                <div class="field">
                    <label>Tabelle</label>
                    <Textarea
                        v-model="bulkTablesText"
                        rows="7"
                        placeholder="Inserisci una tabella per riga oppure separate da virgola"
                    />
                </div>

                <small>
                    Per sincronizzazioni dump le colonne chiave non sono necessarie.
                </small>
            </div>

            <div v-else>
                <div class="field">
                    <label>Tabella</label>
                    <InputText v-model="tableForm.tabella" />
                </div>

                <div v-if="selectedGroupRequiresKeyColumns" class="field">
                    <label>Colonne chiave</label>
                    <InputText v-model="tableForm.colonne_chiave" placeholder="Es. id oppure id,codice" />
                </div>

                <small v-if="isSelectedGroupMove">
                    Per sincronizzazioni move le colonne di controllo vengono rilevate
                    automaticamente dagli indici univoci della tabella di destinazione.
                </small>

                <div class="field">
                    <label>Ordine</label>
                    <InputText v-model="tableForm.ordine" type="number" />
                </div>

                <div class="field">
                    <label>Attivo</label>
                    <InputSwitch v-model="tableForm.attivo" />
                </div>
            </div>

            <template #footer>
                <Button label="Annulla" class="p-button-secondary" @click="closeTableModal" />

                <Button
                    :label="editingTableId ? 'Salva' : 'Aggiungi'"
                    :icon="editingTableId ? 'pi pi-save' : 'pi pi-plus'"
                    :loading="savingTable"
                    @click="saveTable"
                />
            </template>
        </Dialog>

        <Dialog
            :header="notificationsModalTitle"
            v-model:visible="showNotificationsModal"
            :modal="true"
            :style="{ width: '550px' }"
        >
            <div class="field">
                <label>Destinatari riepilogo</label>

                <Chips
                    v-model="notificationsForm.destinatari_riepilogo"
                    separator=","
                    :add-on-blur="true"
                    placeholder="Aggiungi un indirizzo e premi Invio"
                />

                <small>Una mail al termine di ogni sincronizzazione, con esito e dettaglio delle tabelle. Lascia vuoto per non inviarla.</small>
            </div>

            <div class="field">
                <label>Destinatari errori</label>

                <Chips
                    v-model="notificationsForm.destinatari_errori"
                    separator=","
                    :add-on-blur="true"
                    placeholder="Aggiungi un indirizzo e premi Invio"
                />

                <small>Una mail solo quando la sincronizzazione fallisce o ci sono tabelle in errore. Lascia vuoto per non inviarla.</small>
            </div>

            <template #footer>
                <Button label="Annulla" class="p-button-secondary" @click="closeNotificationsModal" />

                <Button
                    label="Salva"
                    icon="pi pi-save"
                    :loading="savingNotifications"
                    @click="saveNotifications"
                />
            </template>
        </Dialog>

        <Dialog
            header="Tipi di sincronizzazione"
            v-model:visible="showTypesInfoModal"
            :modal="true"
            :style="{ width: '550px' }"
        >
            <DataTable :value="syncTypes" responsiveLayout="scroll" class="sync-table">
                <Column field="nome" header="Nome" />
                <Column field="descrizione" header="Descrizione" />
            </DataTable>
        </Dialog>
    </div>
</template>

<script>
import DatabaseSyncService from './database-sync-service';

export default {
    name: 'DatabaseSyncConf',

    data() {
        return {
            loading: false,
            savingGroup: false,
            savingTable: false,
            editingGroupId: null,
            editingTableId: null,
            runningGroupId: null,
            savingNotifications: false,
            showGroupModal: false,
            showTableModal: false,
            showTypesInfoModal: false,
            showNotificationsModal: false,
            tableSearch: '',
            activeGroupIndex: null,
            notificationsGroup: null,
            notificationsForm: {
                destinatari_riepilogo: [],
                destinatari_errori: []
            },
            selectedGroup: null,
            groups: [],
            syncTypes: [],
            databases: [],
            bulkTablesText: '',
            groupForm: {
                gruppo: '',
                database_sync_type_id: null,
                sorgente: null,
                destinazione: null,
                attivo: true,
                cron_expression: '*/5 * * * *'
            },
            cronPreviewValid: false,
            cronPreviewDates: [],
            cronPreviewLoading: false,
            cronPreviewTimeout: null,
            cronPresets: [
                { label: 'Ogni 5 min', value: '*/5 * * * *' },
                { label: 'Ogni 15 min', value: '*/15 * * * *' },
                { label: 'Ogni 30 min', value: '*/30 * * * *' },
                { label: 'Ogni ora', value: '0 * * * *' },
                { label: 'Ogni 15 min, 9-18 lun-ven', value: '*/15 9-18 * * 1-5' },
                { label: 'Ogni notte alle 3', value: '0 3 * * *' },
                { label: 'Lunedì alle 6', value: '0 6 * * 1' }
            ],
            tableForm: {
                tabella: '',
                colonne_chiave: '',
                ordine: 0,
                attivo: true
            }
        };
    },

    computed: {
        isSelectedGroupDump() {
            return this.selectedGroup && this.selectedGroup.tipo === 'dump';
        },

        isSearching() {
            return this.tableSearch.trim().length >= 2;
        },

        /**
         * Cerca la tabella fra tutti i gruppi gia' caricati: nessuna chiamata al
         * backend. Oltre a dire dove si trova, dice se viene davvero
         * sincronizzata: una riga spenta, o un gruppo disattivato, significa che
         * quella tabella non viene copiata anche se risulta configurata.
         */
        searchResults() {
            if (!this.isSearching) {
                return [];
            }

            const term = this.tableSearch.trim().toLowerCase();
            const results = [];

            this.groups.forEach((group, index) => {
                (group.tabelle || []).forEach((table) => {
                    if (!table.tabella || !table.tabella.toLowerCase().includes(term)) {
                        return;
                    }

                    let stato = 'Sincronizzata';
                    let statoClass = 'sync-search-ok';

                    if (!group.attivo) {
                        stato = 'Gruppo disattivato';
                        statoClass = 'sync-search-ko';
                    } else if (!table.attivo) {
                        stato = 'Riga disattivata';
                        statoClass = 'sync-search-warn';
                    }

                    results.push({
                        groupId: group.id,
                        groupIndex: index,
                        gruppo: group.gruppo,
                        tabella: table.tabella,
                        tipo: group.tipo,
                        sorgente: group.sorgente_nome,
                        destinazione: group.destinazione_nome,
                        cron: group.cron_expression,
                        colonneChiave: table.colonne_chiave,
                        stato: stato,
                        statoClass: statoClass
                    });
                });
            });

            // Prima le corrispondenze esatte, poi in ordine alfabetico.
            return results.sort((a, b) => {
                const exactA = a.tabella.toLowerCase() === term ? 0 : 1;
                const exactB = b.tabella.toLowerCase() === term ? 0 : 1;

                if (exactA !== exactB) {
                    return exactA - exactB;
                }

                return a.tabella.localeCompare(b.tabella) || a.gruppo.localeCompare(b.gruppo);
            });
        },

        isSelectedGroupMove() {
            return this.selectedGroup && this.selectedGroup.tipo === 'move';
        },

        // Allineato al backend: per dump e move le colonne chiave non vengono
        // salvate. Il move ricava da solo le colonne di controllo dagli indici
        // univoci della tabella di destinazione.
        selectedGroupRequiresKeyColumns() {
            return this.selectedGroup && !['dump', 'move'].includes(this.selectedGroup.tipo);
        },

        groupModalTitle() {
            return this.editingGroupId ? 'Modifica gruppo sincronizzazione' : 'Nuovo gruppo sincronizzazione';
        },

        tableModalTitle() {
            if (this.editingTableId) {
                return this.isSelectedGroupDump ? 'Modifica tabella' : 'Modifica tabella incremental';
            }

            return this.isSelectedGroupDump ? 'Aggiungi tabelle' : 'Aggiungi tabella';
        },

        notificationsModalTitle() {
            return this.notificationsGroup
                ? `Notifiche email - ${this.notificationsGroup.gruppo}`
                : 'Notifiche email';
        }
    },

    mounted() {
        this.loadGroups();
        this.loadSyncTypes();
        this.loadDatabases();
    },

    methods: {
        async loadGroups() {
            this.loading = true;

            try {
                const response = await DatabaseSyncService.getGroups();
                this.groups = response.data.data || response.data;
            } catch (error) {
                console.error(error);
                alert('Errore durante il caricamento dei gruppi');
            } finally {
                this.loading = false;
            }
        },

        async loadSyncTypes() {
            try {
                const response = await DatabaseSyncService.getSyncTypes();
                this.syncTypes = response.data.data || [];
            } catch (error) {
                console.error(error);
                alert('Errore durante il caricamento dei tipi di sincronizzazione');
            }
        },

        async loadDatabases() {
            try {
                const response = await DatabaseSyncService.getDatabases();
                this.databases = response.data.data || [];
            } catch (error) {
                console.error(error);
                alert('Errore durante il caricamento dei database');
            }
        },

        openGroupModal() {
            this.editingGroupId = null;

            this.groupForm = {
                gruppo: '',
                database_sync_type_id: this.syncTypes.length ? this.syncTypes[0].id : null,
                sorgente: null,
                destinazione: null,
                attivo: true,
                cron_expression: '*/5 * * * *'
            };

            this.refreshCronPreview();
            this.showGroupModal = true;
        },

        openEditGroupModal(group) {
            if (group.in_esecuzione) {
                alert('Non puoi modificare un gruppo mentre è in esecuzione');
                return;
            }

            this.editingGroupId = group.id;

            this.groupForm = {
                gruppo: group.gruppo,
                database_sync_type_id: group.database_sync_type_id,
                sorgente: group.sorgente_id,
                destinazione: group.destinazione_id,
                attivo: !!group.attivo,
                cron_expression: group.cron_expression || '*/5 * * * *'
            };

            this.refreshCronPreview();
            this.showGroupModal = true;
        },

        closeGroupModal() {
            this.showGroupModal = false;
            this.editingGroupId = null;
        },

        onCronExpressionInput() {
            // debounce: si aspetta che l'utente smetta di digitare prima di
            // interrogare il backend per la validazione e l'anteprima
            clearTimeout(this.cronPreviewTimeout);

            this.cronPreviewLoading = true;
            this.cronPreviewTimeout = setTimeout(() => this.refreshCronPreview(), 400);
        },

        applyCronPreset(value) {
            this.groupForm.cron_expression = value;
            this.refreshCronPreview();
        },

        async refreshCronPreview() {
            clearTimeout(this.cronPreviewTimeout);

            const expression = (this.groupForm.cron_expression || '').trim();

            if (!expression) {
                this.cronPreviewValid = false;
                this.cronPreviewDates = [];
                this.cronPreviewLoading = false;
                return;
            }

            this.cronPreviewLoading = true;

            try {
                const response = await DatabaseSyncService.previewCron(expression);

                this.cronPreviewValid = !!response.data.valida;
                this.cronPreviewDates = response.data.prossime_esecuzioni || [];
            } catch (error) {
                this.cronPreviewValid = false;
                this.cronPreviewDates = [];
            } finally {
                this.cronPreviewLoading = false;
            }
        },

        async saveGroup() {
            if (
                !this.groupForm.gruppo ||
                !this.groupForm.database_sync_type_id ||
                !this.groupForm.sorgente ||
                !this.groupForm.destinazione
            ) {
                alert('Compila gruppo, tipo, sorgente e destinazione');
                return;
            }

            if (this.groupForm.sorgente === this.groupForm.destinazione) {
                alert('Il database sorgente e il database destinazione devono essere diversi');
                return;
            }

            const cronExpression = (this.groupForm.cron_expression || '').trim();

            if (!cronExpression) {
                alert('Inserisci la pianificazione (espressione cron)');
                return;
            }

            if (!this.cronPreviewValid) {
                alert('L’espressione cron non è valida');
                return;
            }

            this.savingGroup = true;

            const payload = {
                gruppo: this.groupForm.gruppo,
                database_sync_type_id: this.groupForm.database_sync_type_id,
                sorgente: this.groupForm.sorgente,
                destinazione: this.groupForm.destinazione,
                attivo: this.groupForm.attivo ? 1 : 0,
                cron_expression: cronExpression
            };

            try {
                if (this.editingGroupId) {
                    await DatabaseSyncService.updateGroup(this.editingGroupId, payload);
                } else {
                    await DatabaseSyncService.createGroup(payload);
                }

                this.showGroupModal = false;
                this.editingGroupId = null;
                await this.loadGroups();
            } catch (error) {
                const message = error.response && error.response.data && error.response.data.error
                    ? error.response.data.error
                    : this.editingGroupId
                        ? 'Errore durante la modifica del gruppo'
                        : 'Errore durante la creazione del gruppo';

                alert(message);
            } finally {
                this.savingGroup = false;
            }
        },

        openTableModal(group) {
            if (group.in_esecuzione) {
                alert('Non puoi aggiungere tabelle mentre il gruppo è in esecuzione');
                return;
            }

            this.selectedGroup = group;
            this.editingTableId = null;

            this.tableForm = {
                tabella: '',
                colonne_chiave: '',
                ordine: group.tabelle ? group.tabelle.length + 1 : 1,
                attivo: true
            };

            this.bulkTablesText = '';
            this.showTableModal = true;
        },

        openEditTableModal(group, table) {
            if (group.in_esecuzione) {
                alert('Non puoi modificare una tabella mentre il gruppo è in esecuzione');
                return;
            }

            this.selectedGroup = group;
            this.editingTableId = table.id;

            this.tableForm = {
                tabella: table.tabella,
                colonne_chiave: table.colonne_chiave || '',
                ordine: table.ordine,
                attivo: !!table.attivo
            };

            this.bulkTablesText = '';
            this.showTableModal = true;
        },

        closeTableModal() {
            this.showTableModal = false;
            this.editingTableId = null;
            this.selectedGroup = null;
            this.bulkTablesText = '';
        },

        async saveTable() {
            if (!this.selectedGroup) {
                return;
            }

            this.savingTable = true;

            try {
                if (this.editingTableId) {
                    if (!this.tableForm.tabella) {
                        alert('Inserisci il nome della tabella');
                        return;
                    }

                    if (this.selectedGroupRequiresKeyColumns && !this.tableForm.colonne_chiave) {
                        alert('Inserisci le colonne chiave');
                        return;
                    }

                    await DatabaseSyncService.updateTable(this.editingTableId, {
                        tabella: this.tableForm.tabella,
                        colonne_chiave: this.selectedGroupRequiresKeyColumns ? this.tableForm.colonne_chiave : null,
                        ordine: Number(this.tableForm.ordine) || 0,
                        attivo: this.tableForm.attivo ? 1 : 0
                    });

                    this.closeTableModal();
                    await this.loadGroups();

                    alert('Tabella modificata correttamente');

                    return;
                }

                let tabelle = [];

                if (this.isSelectedGroupDump) {
                    const names = this.bulkTablesText
                        .split(/[\n,]+/)
                        .map((table) => table.trim())
                        .filter((table) => table !== '');

                    if (names.length === 0) {
                        alert('Inserisci almeno una tabella');
                        return;
                    }

                    tabelle = names.map((table, index) => {
                        return {
                            tabella: table,
                            colonne_chiave: null,
                            ordine: (this.selectedGroup.tabelle ? this.selectedGroup.tabelle.length : 0) + index + 1,
                            attivo: 1
                        };
                    });
                } else {
                    if (!this.tableForm.tabella) {
                        alert('Inserisci il nome della tabella');
                        return;
                    }

                    if (this.selectedGroupRequiresKeyColumns && !this.tableForm.colonne_chiave) {
                        alert('Inserisci le colonne chiave');
                        return;
                    }

                    tabelle = [
                        {
                            tabella: this.tableForm.tabella,
                            colonne_chiave: this.selectedGroupRequiresKeyColumns ? this.tableForm.colonne_chiave : null,
                            ordine: Number(this.tableForm.ordine) || 0,
                            attivo: this.tableForm.attivo ? 1 : 0
                        }
                    ];
                }

                await DatabaseSyncService.addTable(this.selectedGroup.id, {
                    tabelle: tabelle
                });

                this.closeTableModal();
                await this.loadGroups();

                alert('Tabelle aggiunte correttamente');
            } catch (error) {
                const message = error.response && error.response.data && error.response.data.error
                    ? error.response.data.error
                    : this.editingTableId
                        ? 'Errore durante la modifica della tabella'
                        : 'Errore durante l’aggiunta delle tabelle';

                alert(message);
            } finally {
                this.savingTable = false;
            }
        },

        async deleteTable(tableId) {
            if (!confirm('Vuoi eliminare questa tabella dal gruppo?')) {
                return;
            }

            try {
                await DatabaseSyncService.deleteTable(tableId);
                await this.loadGroups();
            } catch (error) {
                const message = error.response && error.response.data && error.response.data.error
                    ? error.response.data.error
                    : 'Errore durante l’eliminazione della tabella';

                alert(message);
            }
        },

        getTableRowClass(rowData) {
            return rowData.attivo ? '' : 'sync-table-row-disabled';
        },

        async updateTableStatus(table, value) {
            const oldValue = table.attivo;
            const newValue = value ? 1 : 0;

            table.attivo = newValue;

            try {
                await DatabaseSyncService.updateTableStatus(table.id, newValue);
            } catch (error) {
                table.attivo = oldValue;

                const message = error.response && error.response.data && error.response.data.error
                    ? error.response.data.error
                    : 'Errore durante l’aggiornamento dello stato della tabella';

                alert(message);
            }
        },

        async updateGroupStatus(group, value) {
            const oldValue = group.attivo;
            const newValue = value ? 1 : 0;

            group.attivo = newValue;

            try {
                await DatabaseSyncService.updateGroupStatus(group.id, newValue);
            } catch (error) {
                group.attivo = oldValue;

                const message = error.response && error.response.data && error.response.data.error
                    ? error.response.data.error
                    : 'Errore durante l’aggiornamento dello stato del gruppo';

                alert(message);
            }
        },

        async runGroupNow(group) {
            if (group.in_esecuzione) {
                alert('Il gruppo è già in esecuzione');
                return;
            }

            if (!group.attivo) {
                alert('Non puoi eseguire un gruppo disattivato');
                return;
            }

            if (!confirm(`Vuoi eseguire ora la sincronizzazione del gruppo "${group.gruppo}"?`)) {
                return;
            }

            this.runningGroupId = group.id;

            try {
                await DatabaseSyncService.runGroupNow(group.id);
                await this.loadGroups();

                alert('Sincronizzazione pianificata correttamente. Verrà avviata dallo scheduler entro un minuto.');
            } catch (error) {
                const message = error.response && error.response.data && error.response.data.error
                    ? error.response.data.error
                    : 'Errore durante l’avvio della sincronizzazione';

                alert(message);
            } finally {
                this.runningGroupId = null;
            }
        },

        getTablesCount(group) {
            return group.tabelle ? group.tabelle.length : 0;
        },

        async deleteGroup(groupId) {
            if (!confirm('Vuoi eliminare questo gruppo di sincronizzazione?')) {
                return;
            }

            try {
                await DatabaseSyncService.deleteGroup(groupId);
                await this.loadGroups();
            } catch (error) {
                const message = error.response && error.response.data && error.response.data.error
                    ? error.response.data.error
                    : 'Errore durante l’eliminazione del gruppo';

                alert(message);
            }
        },

        openGroupFromSearch(result) {
            this.activeGroupIndex = result.groupIndex;
            this.tableSearch = '';

            this.$nextTick(() => {
                const el = document.querySelector('.p-accordion-tab:nth-child(' + (result.groupIndex + 1) + ')');

                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            });
        },

        getGroupStatusLabel(group) {
            if (group.in_esecuzione) {
                return 'In esecuzione';
            }

            // Job gia' accodato ma non ancora preso in carico da un worker:
            // senza questo stato sarebbe indistinguibile da un gruppo fermo.
            if (group.in_coda) {
                return 'In coda';
            }

            return 'Fermo';
        },

        getGroupStatusClass(group) {
            if (group.in_esecuzione) {
                return 'sync-group-running';
            }

            if (group.in_coda) {
                return 'sync-group-queued';
            }

            return 'sync-group-stopped';
        },

        hasNotificationsEnabled(group) {
            return !!(
                group.notifiche &&
                (group.notifiche.destinatari_riepilogo || group.notifiche.destinatari_errori)
            );
        },

        parseRecipientsList(value) {
            return value
                ? value.split(',').map((email) => email.trim()).filter((email) => email !== '')
                : [];
        },

        openNotificationsModal(group) {
            this.notificationsGroup = group;

            const notifiche = group.notifiche || {};

            this.notificationsForm = {
                destinatari_riepilogo: this.parseRecipientsList(notifiche.destinatari_riepilogo),
                destinatari_errori: this.parseRecipientsList(notifiche.destinatari_errori)
            };

            this.showNotificationsModal = true;
        },

        closeNotificationsModal() {
            this.showNotificationsModal = false;
            this.notificationsGroup = null;
        },

        async saveNotifications() {
            if (!this.notificationsGroup) {
                return;
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            const destinatariRiepilogo = this.notificationsForm.destinatari_riepilogo
                .map((email) => email.trim())
                .filter((email) => email !== '');

            const destinatariErrori = this.notificationsForm.destinatari_errori
                .map((email) => email.trim())
                .filter((email) => email !== '');

            const invalid = destinatariRiepilogo
                .concat(destinatariErrori)
                .filter((email) => !emailRegex.test(email));

            if (invalid.length > 0) {
                alert('Indirizzi email non validi: ' + invalid.join(', '));
                return;
            }

            this.savingNotifications = true;

            try {
                await DatabaseSyncService.updateNotifications(this.notificationsGroup.id, {
                    destinatari_riepilogo: destinatariRiepilogo.join(','),
                    destinatari_errori: destinatariErrori.join(',')
                });

                this.closeNotificationsModal();
                await this.loadGroups();
            } catch (error) {
                const message = error.response && error.response.data && error.response.data.error
                    ? error.response.data.error
                    : 'Errore durante il salvataggio delle notifiche';

                alert(message);
            } finally {
                this.savingNotifications = false;
            }
        }
    }
};
</script>

<style>
.page-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    margin-bottom: 20px !important;
}

.group-info {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 12px !important;
    margin-bottom: 20px !important;
}

.table-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    margin-bottom: 15px !important;
}

.form-grid {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 15px !important;
}

.field {
    display: flex !important;
    flex-direction: column !important;
    margin-bottom: 15px !important;
}

.field-full {
    grid-column: 1 / -1 !important;
}

.sync-cron-value {
    background-color: #f4f4f5 !important;
    border: 1px solid #e4e4e7 !important;
    border-radius: 4px !important;
    padding: 1px 6px !important;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace !important;
    font-size: 12px !important;
}

.sync-cron-input {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace !important;
}

.sync-cron-legend {
    color: #777777 !important;
    margin-top: 4px !important;
}

.sync-cron-presets {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 6px !important;
    margin-top: 8px !important;
}

.sync-cron-preset-button {
    padding: 4px 12px !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    color: #3f3f46 !important;
    background-color: #f4f4f5 !important;
    border: 1px solid #d4d4d8 !important;
    border-radius: 14px !important;
    cursor: pointer !important;
    transition: background-color 0.15s, border-color 0.15s !important;
}

.sync-cron-preset-button:hover {
    background-color: #e4e4e7 !important;
    border-color: #a1a1aa !important;
}

.sync-cron-preset-button-active {
    background-color: #e0f2fe !important;
    border-color: #38bdf8 !important;
    color: #075985 !important;
}

.sync-cron-preview {
    margin-top: 10px !important;
    padding: 8px 12px !important;
    border-radius: 6px !important;
    font-size: 13px !important;
    display: flex !important;
    flex-wrap: wrap !important;
    align-items: center !important;
    gap: 8px !important;
}

.sync-cron-preview-valid {
    background-color: #f0fdf4 !important;
    border: 1px solid #bbf7d0 !important;
    color: #166534 !important;
}

.sync-cron-preview-invalid {
    background-color: #fef2f2 !important;
    border: 1px solid #fecaca !important;
    color: #b91c1c !important;
    font-weight: 600 !important;
}

.sync-cron-preview-loading {
    background-color: #fafafa !important;
    border: 1px solid #e4e4e7 !important;
    color: #71717a !important;
}

.sync-cron-date {
    background-color: #ffffff !important;
    border: 1px solid #bbf7d0 !important;
    border-radius: 4px !important;
    padding: 1px 6px !important;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace !important;
    font-size: 12px !important;
}

.field label {
    font-weight: 600 !important;
    margin-bottom: 6px !important;
}

.sync-help-icon {
    margin-left: 6px !important;
    cursor: pointer !important;
    color: #2196f3 !important;
    font-size: 14px !important;
}

.sync-table .p-datatable-thead > tr > th {
    background-color: #f4f4f4 !important;
    color: #000000 !important;
    font-weight: 700 !important;
    border: 1px solid #dcdcdc !important;
    /* intestazione sempre visibile mentre si scorrono le righe della tabella */
    position: sticky !important;
    top: 0 !important;
    z-index: 1 !important;
}

/* in modalita' scrollable la tabella diventa display:block e perderebbe
   la larghezza piena del contenitore */
.sync-table .p-datatable-table {
    width: 100% !important;
}

/* In modalita' scrollable ogni cella e' un flex item con base 0: senza
   min-width un nome di tabella lungo allarga la cella oltre la sua base e
   ogni riga finisce per avere larghezze diverse dall'intestazione. Fissando
   le colonne strette e lasciando espandere solo "Tabella" le righe restano
   allineate e i nomi lunghi vanno a capo invece di sfondare. */
.sync-table.p-datatable-scrollable .p-datatable-thead > tr > th,
.sync-table.p-datatable-scrollable .p-datatable-tbody > tr > td {
    min-width: 0 !important;
    overflow-wrap: anywhere !important;
}

.sync-table.p-datatable-scrollable .p-datatable-thead > tr > th:nth-child(1),
.sync-table.p-datatable-scrollable .p-datatable-tbody > tr > td:nth-child(1) {
    flex: 0 0 90px !important;
}

.sync-table.p-datatable-scrollable .p-datatable-thead > tr > th:nth-child(2),
.sync-table.p-datatable-scrollable .p-datatable-tbody > tr > td:nth-child(2) {
    flex: 1 1 0 !important;
}

.sync-table.p-datatable-scrollable .p-datatable-thead > tr > th:nth-child(3),
.sync-table.p-datatable-scrollable .p-datatable-tbody > tr > td:nth-child(3) {
    flex: 0 1 220px !important;
}

.sync-table.p-datatable-scrollable .p-datatable-thead > tr > th:nth-child(4),
.sync-table.p-datatable-scrollable .p-datatable-tbody > tr > td:nth-child(4) {
    flex: 0 0 100px !important;
}

.sync-table.p-datatable-scrollable .p-datatable-thead > tr > th:nth-child(5),
.sync-table.p-datatable-scrollable .p-datatable-tbody > tr > td:nth-child(5) {
    flex: 0 0 110px !important;
}

.sync-table .p-column-title {
    color: #000000 !important;
}

.loading-box,
.empty-box {
    padding: 20px !important;
    background: #ffffff !important;
    border-radius: 6px !important;
}

.sync-table-row-disabled {
    opacity: 0.45 !important;
    background-color: #f5f5f5 !important;
}

.sync-table-row-disabled td {
    color: #777777 !important;
    text-decoration: line-through !important;
}

.sync-group-header {
    width: 100% !important;
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    gap: 12px !important;
}

.sync-group-header-left {
    display: flex !important;
    align-items: center !important;
    gap: 10px !important;
    flex-wrap: wrap !important;
}

.sync-group-table-count {
    font-size: 12px !important;
    font-weight: 600 !important;
    background-color: #eeeeee !important;
    color: #333333 !important;
    padding: 3px 8px !important;
    border-radius: 12px !important;
}

.sync-group-header-actions {
    margin-left: auto !important;
    display: flex !important;
    align-items: center !important;
    gap: 6px !important;
}

.sync-group-running,
.sync-group-queued,
.sync-group-stopped,
.sync-group-active,
.sync-group-inactive {
    font-size: 12px !important;
    font-weight: 700 !important;
    padding: 3px 8px !important;
    border-radius: 12px !important;
}

.sync-group-running {
    background-color: #fff4d6 !important;
    color: #9a6a00 !important;
}

.sync-search-box {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
}

.sync-search-field {
    flex: 1 1 auto;
    max-width: 560px;
}

.sync-search-field input {
    width: 100%;
}

.sync-search-results {
    border: 1px solid #dfe3e8;
    border-radius: 6px;
    background: #fff;
    margin-bottom: 20px;
    overflow: hidden;
}

.sync-search-count {
    padding: 8px 14px;
    font-size: 12px;
    font-weight: 600;
    color: #5f6b7a;
    background: #f5f7f9;
    border-bottom: 1px solid #e6eaee;
}

.sync-search-empty {
    padding: 16px;
    color: #5f6b7a;
}

.sync-search-row {
    padding: 10px 14px;
    border-bottom: 1px solid #eef1f4;
    cursor: pointer;
}

.sync-search-row:last-child {
    border-bottom: none;
}

.sync-search-row:hover {
    background: #f7faff;
}

.sync-search-main {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
}

.sync-search-group {
    font-size: 12px;
    font-weight: 600;
    color: #0b5cab;
    background: #e7f1ff;
    padding: 2px 8px;
    border-radius: 10px;
}

.sync-search-details {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-top: 4px;
    font-size: 12px;
    color: #5f6b7a;
}

.sync-search-ok,
.sync-search-warn,
.sync-search-ko {
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
}

.sync-search-ok {
    background: #dff5e1;
    color: #1b7f32;
}

.sync-search-warn {
    background: #fff4d6;
    color: #9a6a00;
}

.sync-search-ko {
    background: #fde8e8;
    color: #a61b1b;
}

.sync-group-queued {
    background-color: #ede9fe !important;
    color: #5b21b6 !important;
}

.sync-group-stopped {
    background-color: #e7f1ff !important;
    color: #0b5cab !important;
}

.sync-group-active {
    background-color: #dff5e1 !important;
    color: #1b7f32 !important;
}

.sync-group-inactive {
    background-color: #eeeeee !important;
    color: #666666 !important;
}

.group-active-box {
    display: flex !important;
    align-items: center !important;
    gap: 10px !important;
}

.sync-table-actions {
    display: flex !important;
    align-items: center !important;
    gap: 6px !important;
}

.notifications-box {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    gap: 12px !important;
    padding: 10px 14px !important;
    background-color: #fafafa !important;
    border: 1px solid #e4e4e7 !important;
    border-radius: 6px !important;
    margin-bottom: 20px !important;
}

.notifications-info {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    flex-wrap: wrap !important;
}

.sync-notify-line {
    display: inline-flex !important;
    align-items: center !important;
    gap: 6px !important;
}

.sync-notify-badge {
    font-size: 12px !important;
    font-weight: 700 !important;
    padding: 3px 8px !important;
    border-radius: 12px !important;
}

.sync-notify-badge-summary {
    background-color: #dff5e1 !important;
    color: #1b7f32 !important;
}

.sync-notify-badge-error {
    background-color: #fdecea !important;
    color: #b3261e !important;
}

.sync-notify-recipients {
    font-size: 12px !important;
    color: #666666 !important;
    word-break: break-all !important;
}

.sync-notify-off {
    font-size: 12px !important;
    color: #888888 !important;
}

@media (max-width: 768px) {
    .group-info,
    .form-grid {
        grid-template-columns: 1fr !important;
    }

    .page-header,
    .table-header {
        flex-direction: column !important;
        align-items: flex-start !important;
        gap: 10px !important;
    }

    .sync-group-header {
        flex-direction: column !important;
        align-items: flex-start !important;
    }

    .sync-group-header-actions {
        margin-left: 0 !important;
    }

    .notifications-box {
        flex-direction: column !important;
        align-items: flex-start !important;
    }
}
</style>